// src/components/dashboard/ProgressCharts.js
import React from 'react';
import { BarChart, Bar, XAxis, YAxis, CartesianGrid, Tooltip, ResponsiveContainer, LineChart, Line, PieChart, Pie, Cell } from 'recharts';
import { Clock, Target, Trophy, BookOpen } from 'lucide-react';
import { getGameName } from '../../utils/gameNames';

const ProgressCharts = ({ dashboardData }) => {
  if (!dashboardData) return null;

  // Format time for charts
  const formatTimeMinutes = (seconds) => Math.round(seconds / 60);

  // Prepare daily activity data
  const dailyData = dashboardData.dailyActivity?.map(day => ({
    day: day.dayName,
    time: formatTimeMinutes(day.totalTime),
    sessions: day.sessions,
    score: day.totalScore
  })) || [];

  // Prepare games progress data - now using shared utility
  const gamesData = dashboardData.gamesProgress?.map(game => {
    return {
      name: getGameName(game.gameType),
      level: game.currentLevel,
      stars: game.totalStars,
      accuracy: game.averageAccuracy,
      completed: game.completedLevels
    };
  }) || [];

  // Colors for charts
  const colors = ['#8B5CF6', '#06B6D4', '#10B981', '#F59E0B', '#EF4444', '#EC4899'];

  // Performance trend data
  const performanceTrendIcon = {
    'improving': <Target className="w-5 h-5 text-green-500" />,
    'declining': <Target className="w-5 h-5 text-red-500" />,
    'stable': <Target className="w-5 h-5 text-blue-500" />
  };

  const performanceTrendText = {
    'improving': 'Performance is improving! 📈',
    'declining': 'Needs attention 📉',
    'stable': 'Steady progress 📊'
  };

  const performanceTrendColor = {
    'improving': 'text-green-600 bg-green-50 border-green-200',
    'declining': 'text-red-600 bg-red-50 border-red-200',
    'stable': 'text-blue-600 bg-blue-50 border-blue-200'
  };

  return (
    <div className="space-y-8">
      {/* Performance Trend Alert */}
      {dashboardData.performanceTrend && (
        <div className={`p-4 rounded-xl border-2 flex items-center gap-3 ${performanceTrendColor[dashboardData.performanceTrend]}`}>
          {performanceTrendIcon[dashboardData.performanceTrend]}
          <span className="font-semibold">
            {performanceTrendText[dashboardData.performanceTrend]}
          </span>
        </div>
      )}

      {/* Daily Activity Chart */}
      <div className="bg-white rounded-2xl p-6 shadow-xl border-4 border-purple-100">
        <div className="flex items-center gap-3 mb-6">
          <div className="bg-purple-100 rounded-xl p-3">
            <Clock className="w-6 h-6 text-purple-600" />
          </div>
          <h2 className="text-2xl font-bold text-gray-800">Daily Activity (Last 7 Days)</h2>
        </div>
        
        <ResponsiveContainer width="100%" height={300}>
          <BarChart data={dailyData}>
            <CartesianGrid strokeDasharray="3 3" />
            <XAxis dataKey="day" />
            <YAxis />
            <Tooltip 
              formatter={(value, name) => [
                name === 'time' ? `${value} minutes` : value,
                name === 'time' ? 'Play Time' : 'Sessions'
              ]}
            />
            <Bar dataKey="time" fill="#8B5CF6" name="time" radius={[4, 4, 0, 0]} />
            <Bar dataKey="sessions" fill="#06B6D4" name="sessions" radius={[4, 4, 0, 0]} />
          </BarChart>
        </ResponsiveContainer>
      </div>

      {/* Games Progress */}
      {gamesData.length > 0 && (
        <div className="bg-white rounded-2xl p-6 shadow-xl border-4 border-blue-100">
          <div className="flex items-center gap-3 mb-6">
            <div className="bg-blue-100 rounded-xl p-3">
              <BookOpen className="w-6 h-6 text-blue-600" />
            </div>
            <h2 className="text-2xl font-bold text-gray-800">Games Progress</h2>
          </div>

          <div className="grid grid-cols-1 lg:grid-cols-2 gap-8">
            {/* Current Levels */}
            <div>
              <h3 className="text-lg font-semibold mb-4 text-gray-700">Current Levels</h3>
              <ResponsiveContainer width="100%" height={250}>
                <BarChart data={gamesData}>
                  <CartesianGrid strokeDasharray="3 3" />
                  <XAxis dataKey="name" />
                  <YAxis />
                  <Tooltip />
                  <Bar dataKey="level" fill="#10B981" radius={[4, 4, 0, 0]} />
                </BarChart>
              </ResponsiveContainer>
            </div>

            {/* Accuracy by Game */}
            <div>
              <h3 className="text-lg font-semibold mb-4 text-gray-700">Accuracy by Game</h3>
              <ResponsiveContainer width="100%" height={250}>
                <LineChart data={gamesData}>
                  <CartesianGrid strokeDasharray="3 3" />
                  <XAxis dataKey="name" />
                  <YAxis domain={[0, 100]} />
                  <Tooltip formatter={(value) => [`${value}%`, 'Accuracy']} />
                  <Line 
                    type="monotone" 
                    dataKey="accuracy" 
                    stroke="#F59E0B" 
                    strokeWidth={3}
                    dot={{ fill: '#F59E0B', strokeWidth: 2, r: 6 }}
                  />
                </LineChart>
              </ResponsiveContainer>
            </div>
          </div>

          {/* Games Summary Cards */}
          <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-4 mt-6">
            {gamesData.map((game, index) => (
              <div key={index} className="bg-gradient-to-br from-gray-50 to-gray-100 rounded-xl p-4 border-2 border-gray-200">
                <h4 className="font-bold text-gray-800 mb-2">{game.name}</h4>
                <div className="space-y-2 text-sm">
                  <div className="flex justify-between">
                    <span className="text-gray-600">Current Level:</span>
                    <span className="font-semibold text-blue-600">{game.level}</span>
                  </div>
                  <div className="flex justify-between">
                    <span className="text-gray-600">Completed:</span>
                    <span className="font-semibold text-green-600">{game.completed} levels</span>
                  </div>
                  <div className="flex justify-between">
                    <span className="text-gray-600">Stars:</span>
                    <span className="font-semibold text-yellow-600">{game.stars} ⭐</span>
                  </div>
                  <div className="flex justify-between">
                    <span className="text-gray-600">Accuracy:</span>
                    <span className="font-semibold text-purple-600">{game.accuracy}%</span>
                  </div>
                </div>
              </div>
            ))}
          </div>
        </div>
      )}

      {/* Weekly Summary */}
      <div className="bg-white rounded-2xl p-6 shadow-xl border-4 border-green-100">
        <div className="flex items-center gap-3 mb-6">
          <div className="bg-green-100 rounded-xl p-3">
            <Trophy className="w-6 h-6 text-green-600" />
          </div>
          <h2 className="text-2xl font-bold text-gray-800">This Week's Summary</h2>
        </div>

        <div className="grid grid-cols-1 md:grid-cols-3 gap-6">
          <div className="text-center p-4 bg-gradient-to-br from-blue-50 to-blue-100 rounded-xl">
            <div className="text-3xl font-bold text-blue-600">
              {Math.round(dashboardData.last7DaysTime / 60)}
            </div>
            <div className="text-blue-700 font-medium">Minutes Played</div>
          </div>
          
          <div className="text-center p-4 bg-gradient-to-br from-purple-50 to-purple-100 rounded-xl">
            <div className="text-3xl font-bold text-purple-600">
              {dashboardData.last7DaysSessions}
            </div>
            <div className="text-purple-700 font-medium">Games Played</div>
          </div>
          
          <div className="text-center p-4 bg-gradient-to-br from-green-50 to-green-100 rounded-xl">
            <div className="text-3xl font-bold text-green-600">
              {Math.round(dashboardData.last7DaysTime / 60 / 7)}
            </div>
            <div className="text-green-700 font-medium">Avg. Daily Minutes</div>
          </div>
        </div>
      </div>
    </div>
  );
};

export default ProgressCharts;